import { Checkbox, Field, Label } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { ReactElement } from 'react';

interface Props<T extends string> {
  options: Array<{ label: string; value: T; checked: boolean }>;
  setChecked: (params: { value: T; checked: boolean }) => void;
  label?: string;
  helperText?: string;
  error?: string;
}

export function CheckboxGroup<T extends string>({
  options,
  setChecked,
  label,
  helperText,
  error,
}: Props<T>): ReactElement {
  return (
    <div className="space-y-2">
      {label && (
        <span className="text-body-medium font-semibold block">{label}</span>
      )}
      {helperText && (
        <span className="text-body-medium text-text-low-priority block">
          {helperText}
        </span>
      )}
      <div className="flex flex-col">
        {options.map((opt) => (
          <Field
            key={opt.value}
            className="flex items-center gap-3 my-2 cursor-pointer"
          >
            <Checkbox
              checked={opt.checked}
              onChange={(checked) => setChecked({ value: opt.value, checked })}
              className="group/checkbox flex size-5 shrink-0 items-center justify-center rounded-xs border border-grey-t-700 transition-colors data-checked:border-primary-400 data-checked:bg-primary-400"
            >
              <CheckIcon className="invisible size-3.5 text-text-filled-component group-data-checked/checkbox:visible" />
            </Checkbox>
            <Label className="cursor-pointer select-none text-sm text-white">
              {opt.label}
            </Label>
          </Field>
        ))}
      </div>
      {error && (
        <div className="flex space-x-1.5 items-start mt-2">
          <InformationCircleIcon className="shrink-0 size-4 text-red-300 mt-[3px]" />
          <span className="text-body-smallest text-red-300">{error}</span>
        </div>
      )}
    </div>
  );
}
